import type { StudentQuestionAnswer, StudentTestExercise } from "p6m-p6u";
import UnderlinedInput from "../../../basic/underlinedInput/UnderlinedInput";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import ExerciseWrapper from "./ExerciseWrapper";
import useExerciseAnswers from "../../../hooks/useExerciseAnswers";
import { getExerciseProgress } from "../../../helpers/OnlineTestHelper";

interface WriteSentenceExerciseProps {
    exercise: StudentTestExercise;
    exerciseIndex: number;
    studentAnswer: StudentQuestionAnswer;
    onAnswerUpdate: (updatedAnswer: StudentQuestionAnswer, exerciseId: string) => void;
}

const WriteSentenceExercise = (props: WriteSentenceExerciseProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const { exerciseId, answers, updateItemAtPosition } = useExerciseAnswers({
        exercise: props.exercise,
        studentAnswer: props.studentAnswer,
        onAnswerUpdate: props.onAnswerUpdate,
    });
    const progress = getExerciseProgress(props.studentAnswer, props.exercise.questionMode);

    if (!exerciseId) return null;

    const renderWordCards = () => {
        if (!answers.length) return null;

        return (
            <div className={`flex flex-col ${getStyleBasedOnBreakpoint(Breakpoint.MD, "gap-m", "gap-s")}`}>
                {answers.map((item, index) => (
                    <div
                        key={item.id ?? index}
                        className={`bg-white border border-p6-grey-light rounded-medium ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "px-m py-s",
                            "px-s py-s"
                        )}`}
                    >
                        <div className="flex flex-col gap-xs">
                            <p className="font-semibold text-m text-p6-grey-dark">
                                {item.question || item.answer || ""}
                            </p>
                            <UnderlinedInput
                                data-testid={`online-test-write-input-${index}`}
                                text={item.studentAnswer ?? ""}
                                setText={(value) => updateItemAtPosition(index, value)}
                                initialLines={2}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <ExerciseWrapper
            data-testid="online-test-exercise-write-sentence"
            number={props.exerciseIndex + 1}
            title={props.exercise.extraComments ?? ""}
            progress={progress}
        >
            {renderWordCards()}
        </ExerciseWrapper>
    );
};

export default WriteSentenceExercise;
